/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import cookie from 'js-cookie';
import { CUSTOMER_VIEW_BLOCK, CUSTOMER_VIEW_CUSTOMERS_LIST, CUSTOMER_VIEW_WATCH, API_URL, API_OK, API_EXCEPTION, DEFAULT_ERROR_TEXT } from '../../../Constants';
import '../../../styles.css';
import { useTranslation } from 'react-i18next';
import Device from '../../Device';
import {Button, Box} from '@mui/material';
import DesktopDataGrid from '../../DesktopDataGrid';
import { formatLocalPhone, handleApiErrorMessage } from '../../../Helper';
import { logout } from '../../../features/user';
import { showErrorDialog } from '../../../features/errorDialog';

/**
 * Map a line's `membership` value to a relationship tag (label + css class),
 * indicating how this community is related to the line.
 *   LINE_LINK → the line is specifically assigned to this community.
 *   CUSTOMER  → the line is visible via the customer's community assignment.
 * Returns null for unknown / missing values so no badge renders.
 */
const getLineMembershipTag = (membership) => {
  switch (membership) {
    case 'LINE_LINK':
      return { label: 'משויך למנוי', className: 'membership-tag--line' };
    case 'CUSTOMER':
      return { label: 'דרך הלקוח', className: 'membership-tag--customer' };
    default:
      return null;
  }
};

export default function Customer(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [refreshingSimId, setRefreshingSimId] = useState(null);
  const [simToast, setSimToast] = useState(false);

  // Push network name to the line (SIM refresh)
  const handleRefreshSim = (line) => {
    if (refreshingSimId !== null) return;
    let errorMessage = t(DEFAULT_ERROR_TEXT);
    setRefreshingSimId(line.id);
    axios.post(API_URL + '/pushNetworkName',
      { customer_id: props.customer.id, line_id: line.id },
      { headers: { token: cookie.get('token') } })
      .then(({ data }) => {
        if (data && data.responseStatus === API_OK) {
          setSimToast(true);
          setTimeout(() => setSimToast(false), 2200);
        } else {
          if (data && data.responseStatus === API_EXCEPTION && data.message) errorMessage = data.message;
          handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate);
        }
      })
      .catch(() => handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate))
      .finally(() => setRefreshingSimId(null));
  };

  const columns = [
    { field: 'use_name', headerName: t('Line User Name')},
    {
      field: 'membership',
      headerName: 'שיוך לקהילה',
      renderCell: (params) => {
        const tag = getLineMembershipTag(params.membership);
        return tag ? (
          <span className={'membership-tag ' + tag.className}>{tag.label}</span>
        ) : null;
      }
    },
    { field: 'number', headerName: t('Number'), renderCell: (params) => formatLocalPhone(params.number) },
    { field: 'create_date', headerName: t('Created at')},
    //{ field: 'last_call', headerName: t('Last Call')},
    { 
      field: 'last_call',
      headerName: t('Last Call'),
      renderCell: (params) => {
        return (
          <span className='ci-last-call' style={{ color: ((new Date() - new Date(params.last_call)) / (1000 * 60 * 60 * 24)) > 5 && localStorage.getItem('show_red_inactive_lines') === "true" ? "#DC2626" : "inherit"}}>
            {params.last_call}
          </span>
        );
      }
    },
    {
      field: 'actions', headerName: t('Actions'),
      renderCell: (cellValues, gridFunctions) => {
        return (
          <div className='ci-line-actions'>
            <button className={'ci-act ci-act--device' + (props.expandedRowIds.includes(cellValues.id) ? ' is-open' : '')}
              onClick={(e) => { e.stopPropagation(); gridFunctions.toggleExpandRow(cellValues); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
              {props.expandedRowIds.includes(cellValues.id) ? 'הסתר מכשיר' : 'הצג מכשיר'}
            </button>
            <button className='ci-act ci-act--watch'
              onClick={(e) => { e.stopPropagation(); props.setSelectedLine(cellValues); props.setCustomerView(CUSTOMER_VIEW_WATCH); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 16 14"/></svg>
              מערכת שעון
            </button>
            <button className='ci-act ci-act--block'
              onClick={(e) => { e.stopPropagation(); props.setSelectedLine(cellValues); props.setCustomerView(CUSTOMER_VIEW_BLOCK); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
              רשימת חסימה
            </button>
            <button className='ci-act ci-act--sim'
              disabled={refreshingSimId !== null}
              onClick={(e) => { e.stopPropagation(); handleRefreshSim(cellValues); }}>
              {refreshingSimId === cellValues.id ? (
                <span className='ci-act-spinner' />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
              )}
              {refreshingSimId === cellValues.id ? 'מרענן...' : 'ריענון סים'}
            </button>
            <button className='ci-act ci-act--remove'
              onClick={(e) => { e.stopPropagation(); props.onRequestRemoveLine(cellValues); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              הסר מהקהילה
            </button>
          </div>
        );
      }
    }
  ];

  const renderRowExpansion = (cellValues) => {
    return <Device customer={props.customer} line={cellValues} />
  };

  const customerInactive = (((new Date() - new Date(props.customer.last_out_call)) / (1000 * 60 * 60 * 24)) > 5 && localStorage.getItem('show_red_inactive_lines') === "true");

  return (
    <div className="customer-information-popup ci-redesign">
      {/* ── hero: customer identity ── */}
      <div className='ci-hero'>
        <div className='ci-hero-main'>
          <div className='ci-avatar'>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <div className='ci-id'>
            <div className='ci-name'>{props.customer.name}</div>
            <div className='ci-phone'>{formatLocalPhone(props.customer.phone) || '—'}</div>
          </div>
        </div>

        <div className='ci-stats'>
          <div className='ci-stat'>
            <span className='ci-stat-label'>{t('Last Updated at')}</span>
            <span className='ci-stat-val'>{props.customer.update_date || '—'}</span>
          </div>
          <div className='ci-stat'>
            <span className='ci-stat-label'>{t('Last Call')}</span>
            <span className={'ci-stat-val' + (customerInactive ? ' inactive' : '')}>{props.customer.last_out_call || '—'}</span>
          </div>
        </div>

      </div>

      {/* ── manage watch / block for the whole customer (all lines) ── */}
      <div className='ci-cust-actions'>
        <div className='ci-cust-actions-label'>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
          <span>ניהול עבור כל הלקוח (כל הקווים יחד)</span>
        </div>
        <div className='ci-cust-actions-btns'>
          <button className='ci-cust-act ci-cust-act--watch'
            onClick={() => { props.resetSelectedLine(); props.setCustomerView(CUSTOMER_VIEW_WATCH); }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 16 14"/></svg>
            ניהול מערכת שעון
          </button>
          <button className='ci-cust-act ci-cust-act--block'
            onClick={() => { props.resetSelectedLine(); props.setCustomerView(CUSTOMER_VIEW_BLOCK); }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
            ניהול רשימת חסימה
          </button>
        </div>
      </div>

      <div className='ci-section'>
        <div className='ci-section-head'>
          <div className='ci-section-title-wrap'>
            <h3 className='ci-section-title'>קווים</h3>
            <span className='ci-section-count'>{props.rowCount}</span>
            {props.lineSearch ? <span className='ci-section-filtered'>מסונן לפי חיפוש</span> : null}
          </div>
          <div className='ci-section-sort'>
            <div className='ci-sort-group'>
              <span className='sort-by-label'>{t('Sort by:')}</span>
              <select className='psh-select' value={props.sortColumn} onChange={(e) => props.setSortColumn(e.target.value)}>
                <option value="use_name">{t('Line User Name')}</option>
                <option value="number">{t('Number')}</option>
                <option value="create_date">{t('Created at')}</option>
                <option value="last_call">{t('Last Call')}</option>
              </select>
            </div>
            <div className='ci-sort-group'>
              <span className='sort-by-label'>{t('Sort order:')}</span>
              <select className='psh-select' value={props.sortColumnOrder} onChange={(e) => props.setSortColumnOrder(e.target.value)}>
                <option value="asc">{t('Ascending')}</option>
                <option value="desc">{t('Descending')}</option>
              </select>
            </div>
            <div className='ci-sort-group'>
              <span className='sort-by-label'>הצג לעמוד:</span>
              <select className='psh-select' value={props.pageSize}
                onChange={(e) => { props.setPage(0); props.setPageSize(Number(e.target.value)); }}>
                {(props.pageSizeOptions || []).map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
            </div>
            {/* the API has no per-customer lines export - the file is built in the browser */}
            <button type='button' className='ci-export-btn' onClick={props.onExportLines} disabled={props.exporting}>
              {props.exporting ? (
                <span className='ci-act-spinner' />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              )}
              <span>{props.exporting ? 'מייצא...' : 'ייצוא קווים'}</span>
            </button>
          </div>
        </div>
        <DesktopDataGrid
        entityType="line"
      rowCount={props.rowCount}
      rows={props.rows}
      columns={columns}
      loading={props.loading}
      page={props.page}
      pageSize={props.pageSize}
      onPageChange={(newPage) => {
        props.setPage(newPage);
      }}
      onPageSizeChange={(newPageSize) => {
        props.setPage(0);
        props.setPageSize(Number(newPageSize));
      }}
      pageSizeOptions={props.pageSizeOptions}
      renderExpansion={renderRowExpansion}
      expandedRowIds={props.expandedRowIds}
      setExpandedRowIds={props.setExpandedRowIds}
      sortColumn={props.sortColumn}
      setSortColumn={props.setSortColumn}
      sortColumnOrder={props.sortColumnOrder}
      setSortColumnOrder={props.setSortColumnOrder}
        />
      </div>
      {simToast && createPortal(
        <div className="cat-toast-overlay">
          <div className="cat-toast">
            <div className="cat-toast-check">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div className="cat-toast-text">בקשת ריענון הסים נשלחה בהצלחה</div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
